import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Camera, AlertCircle, CheckCircle, Loader2, X, QrCode } from 'lucide-react';
import { toast } from 'sonner';
import Webcam from 'react-webcam';
import jsQR from 'jsqr';
import api from '../utils/api';

const QRScannerGun = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scannerToken = searchParams.get('token'); // Changed from orgPin to token
  
  const [cameraActive, setCameraActive] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [recentScans, setRecentScans] = useState([]);
  const [validatingToken, setValidatingToken] = useState(true); // Changed from validatingPin
  const [tokenValid, setTokenValid] = useState(false); // Changed from pinValid
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastCheckedIn, setLastCheckedIn] = useState(null);
  const [scanState, setScanState] = useState('idle'); // idle, detecting, success, warning, error
  const [lastError, setLastError] = useState('');
  const [orgName, setOrgName] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [cameraError, setCameraError] = useState('');
  
  const webcamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const scanningRef = useRef(false); // Track scanning state in ref for interval
  const cooldownRef = useRef(false); // Track if we're in cooldown period
  const lastScannedQRRef = useRef(null); // Track last scanned QR to prevent duplicates
  const lastScanTimeRef = useRef(0); // Track when we last scanned

  // Validate token on mount
  useEffect(() => {
    validateScannerToken();
  }, [scannerToken]);

  const validateScannerToken = async () => {
    if (!scannerToken) {
      toast.error('Invalid scanner link - missing token');
      setValidatingToken(false);
      return;
    }

    try {
      // Use v2 PostgreSQL endpoint
      const response = await api.post('/v2/public/check-in/validate-scanner-token', { token: scannerToken });
      
      
      if (response.data.valid) {
        setTokenValid(true);
        setOrgName(response.data.organization?.name || response.data.organizationName);
        setExpiresAt(response.data.session?.expiresAt || response.data.expiresAt);
        toast.success(`Scanner ready for ${response.data.organization?.name || response.data.organizationName}`);
      } else {
        console.error('❌ Token invalid:', response.data.message);
        toast.error(response.data.message || 'Invalid scanner token');
        setTokenValid(false);
      }
    } catch (error) {
      console.error('❌ Token validation error:', error);
      
      let errorMsg = 'Scanner token validation failed';
      try {
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          if (typeof detail === 'string') {
            errorMsg = detail;
          } else if (Array.isArray(detail)) {
            errorMsg = detail.map(e => {
              if (typeof e === 'string') return e;
              if (e.msg) return String(e.msg);
              return 'Validation error';
            }).join(', ');
          } else if (detail && typeof detail === 'object') {
            errorMsg = detail.msg ? String(detail.msg) : 'Validation failed';
          }
        } else if (error.message) {
          errorMsg = String(error.message);
        }
      } catch (parseError) {
        console.error('Error parsing error message:', parseError);
      }
      
      toast.error(errorMsg);
      setTokenValid(false);
    } finally {
      setValidatingToken(false);
    }
  };

  const startCamera = async () => {
    setCameraActive(true);
    
    // Auto-start scanning when camera starts
    setTimeout(() => {
      setScanning(true);
      scanningRef.current = true; // Set ref too for interval
      
      const intervalId = setInterval(() => {
        scanQRCode();
      }, 500);
      
      scanIntervalRef.current = intervalId;
      
      toast.success('Scanning active - point at QR code');
    }, 1500); // Increased delay to ensure camera is ready
  };

  const stopCamera = () => {
    setScanning(false);
    scanningRef.current = false; // Update ref too
    
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    setCameraActive(false);
  };

  // Camera permission / hardware failure — surfaces a clear message instead of a black screen
  const handleUserMediaError = (err) => {
    console.error('[CAMERA] getUserMedia error:', err);
    const name = (err && (err.name || err.message)) ? String(err.name || err.message) : '';
    let msg = 'Unable to access the camera. Please try again.';
    if (/NotAllowed|Permission|SecurityError/i.test(name)) {
      msg = 'Camera access is blocked. Enable the camera permission for BookingFlows in your device Settings, then tap Try again.';
    } else if (/NotFound|DevicesNotFound|Overconstrained/i.test(name)) {
      msg = 'No usable camera was found on this device.';
    } else if (/NotReadable|TrackStart/i.test(name)) {
      msg = 'The camera is being used by another app. Close it and tap Try again.';
    }
    setCameraError(msg);
    setScanning(false);
    scanningRef.current = false;
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
  };

  const retryCamera = () => {
    setCameraError('');
    setCameraActive(false);
    // Re-mount the Webcam so getUserMedia (and the iOS permission prompt) fire again
    setTimeout(() => startCamera(), 150);
  };

  const scanQRCode = () => {
    if (!webcamRef.current || !scanningRef.current || cooldownRef.current) {
      return;
    }

    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      return;
    }

    // Convert base64 image to image data
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: "dontInvert",
      });

      if (code?.data) {
        
        // Check if we're in cooldown OR if this is the same QR code we just scanned
        const now = Date.now();
        const timeSinceLastScan = now - lastScanTimeRef.current;
        const isSameQR = lastScannedQRRef.current === code.data;
        
        // Only process if: not in cooldown AND (different QR OR enough time passed)
        if (!cooldownRef.current && (!isSameQR || timeSinceLastScan > 3000)) {
          // IMMEDIATELY enter cooldown and STOP the scanning interval
          cooldownRef.current = true;
          scanningRef.current = false;
          lastScannedQRRef.current = code.data;
          lastScanTimeRef.current = now;
          
          // STOP the interval completely during cooldown
          if (scanIntervalRef.current) {
            clearInterval(scanIntervalRef.current);
            scanIntervalRef.current = null;
          }
          
          setScanState('detecting'); // Blue state
          handleQRCodeDetected(code.data);
        } else {
        }
      } else {
        setScanState('idle'); // Grey state - no QR code in view
      }
    };
    
    img.onerror = (err) => {
      console.error('[SCAN] Image load error:', err);
    };
    
    img.src = imageSrc;
  };

  const handleQRCodeDetected = async (qrData) => {
    // Already stopped scanning in scanQRCode() to prevent duplicate scans
    setScanning(false);
    
    
    // Vibrate if supported
    if (navigator.vibrate) {
      navigator.vibrate(200);
    }

    try {
      // Extract confirmation code from QR data
      let confirmationCode = qrData;
      
      // Handle different QR code formats
      if (qrData.includes('/check-in/')) {
        // Format: https://domain.com/check-in/BF-ABC123?org=...
        const urlParts = qrData.split('/check-in/')[1];
        confirmationCode = urlParts.split('?')[0]; // Get BF-ABC123
      } else if (qrData.includes('confirmation=')) {
        // Format: ...?confirmation=BF-ABC123&...
        confirmationCode = qrData.split('confirmation=')[1].split('&')[0];
      } else if (qrData.startsWith('BF-')) {
        // Already just the code
        confirmationCode = qrData;
      }
      

      // Check in the customer using the scanner token - PUBLIC endpoint (v2 PostgreSQL)
      const response = await api.post('/v2/public/check-in/scan', {
        confirmationCode,
        scannerToken: scannerToken // Use token instead of PIN
      });
      

      const booking = response.data.booking;
      const alreadyCheckedIn = response.data.alreadyCheckedIn;
      
      // Set appropriate state based on whether already checked in
      if (alreadyCheckedIn) {
        setScanState('warning'); // Yellow/orange state for already checked in
      } else {
        setScanState('success'); // Green state for new check-in
      }
      
      // Show success modal with appropriate message
      setLastCheckedIn({
        customerName: booking.customerName,
        customerEmail: booking.customerEmail,
        serviceName: booking.serviceName,
        alreadyCheckedIn: alreadyCheckedIn
      });
      setShowSuccessModal(true);
      
      // Add to recent scans
      setRecentScans(prev => [{
        id: booking.id,
        confirmationCode: booking.confirmationCode,
        customerName: booking.customerName,
        serviceName: booking.serviceName,
        checkedInAt: new Date().toISOString(),
        status: alreadyCheckedIn ? 'already_checked_in' : 'success'
      }, ...prev.slice(0, 9)]); // Keep last 10
      
      const message = alreadyCheckedIn 
        ? `⚠️ Already checked in: ${booking.customerName}`
        : `✅ ${booking.customerName} checked in!`;
      toast.success(message);
      
      // Start cooldown countdown (4 seconds)
      const cooldownDuration = 4;
      setCooldownSeconds(cooldownDuration);
      
      const countdownInterval = setInterval(() => {
        setCooldownSeconds(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Auto-hide modal and resume scanning after cooldown
      setTimeout(() => {
        setShowSuccessModal(false);
        setScanState('idle');
        setScanning(true);
        scanningRef.current = true;
        cooldownRef.current = false; // Exit cooldown
        setCooldownSeconds(0);
        
        // RESTART the scanning interval
        const intervalId = setInterval(() => {
          scanQRCode();
        }, 500);
        scanIntervalRef.current = intervalId;
      }, cooldownDuration * 1000);
      
    } catch (error) {
      console.error('❌ Check-in error:', error);
      console.error('Error response:', error.response?.data);
      
      // Extract error message - BULLETPROOF conversion to string
      let errorMsg = 'Check-in failed';
      
      try {
        if (error.response?.data?.detail) {
          const detail = error.response.data.detail;
          
          if (typeof detail === 'string') {
            errorMsg = detail;
          } else if (Array.isArray(detail)) {
            // Pydantic validation error array
            errorMsg = detail.map(e => {
              if (typeof e === 'string') return e;
              if (e.msg) return String(e.msg);
              return 'Validation error';
            }).join(', ');
          } else if (detail && typeof detail === 'object') {
            errorMsg = detail.msg ? String(detail.msg) : 'Validation failed';
          }
        } else if (error.message) {
          errorMsg = String(error.message);
        }
      } catch (parseError) {
        console.error('Error parsing error message:', parseError);
        errorMsg = 'Check-in failed';
      }
      
      
      // Ensure everything is a string before setting state
      const errorString = String(errorMsg);
      setLastError(errorString);
      setScanState('error');
      toast.error(errorString);
      
      // Add failed scan to recent scans - ensure error is string
      setRecentScans(prev => [{
        id: Date.now(),
        confirmationCode: String(qrData).substring(0, 20),
        status: 'error',
        error: errorString, // Already a string
        checkedInAt: new Date().toISOString()
      }, ...prev.slice(0, 9)]);
      
      // Start cooldown countdown for errors too (4 seconds)
      const cooldownDuration = 4;
      setCooldownSeconds(cooldownDuration);
      
      const countdownInterval = setInterval(() => {
        setCooldownSeconds(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Resume scanning after 3 seconds
      setTimeout(() => {
        setScanState('idle');
        setLastError('');
        setScanning(true);
        scanningRef.current = true;
        cooldownRef.current = false; // Exit cooldown on error too
        setCooldownSeconds(0);
        
        // RESTART the scanning interval
        const intervalId = setInterval(() => {
          scanQRCode();
        }, 500);
        scanIntervalRef.current = intervalId;
      }, cooldownDuration * 1000);
    }
  };

  useEffect(() => {
    return () => {
      if (scanIntervalRef.current) {
        clearInterval(scanIntervalRef.current);
      }
    };
  }, []);

  if (validatingToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-lg text-gray-600">Validating scanner...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-[0_8px_24px_rgba(0,0,0,0.06)] p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-amber-50 mx-auto mb-4 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-amber-500" />
          </div>
          <h1 className="text-lg font-semibold text-gray-900 mb-2">Session expired</h1>
          <p className="text-sm text-gray-600 mb-6 leading-relaxed">
            Scanner sessions last 12 hours. Open a fresh one from{' '}
            <span className="font-medium text-gray-900">Customers → Check-In → Open Scanner</span>.
          </p>
          <button
            onClick={() => navigate('/admin')}
            className="w-full px-4 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition"
            data-testid="scanner-back-to-dashboard"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      {/* Header */}
      <div className="border-b border-white/5 px-5 sm:px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
            <QrCode className="w-4 h-4 text-blue-400" />
          </div>
          <div className="min-w-0">
            <h1 className="text-sm sm:text-base font-semibold text-white truncate">Scanner</h1>
            <p className="text-xs text-gray-500 truncate">
              {orgName || 'Loading…'}
              {expiresAt && <span className="ml-2 text-gray-600">· session ends {new Date(expiresAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/admin')}
          className="p-2 -mr-2 rounded-md text-gray-500 hover:text-white hover:bg-white/5 transition"
          aria-label="Close scanner"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Camera View */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        {!cameraActive ? (
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 rounded-2xl bg-white/5 mx-auto mb-5 flex items-center justify-center">
              <Camera className="w-7 h-7 text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Ready to scan</h2>
            <p className="text-sm text-gray-400 mb-6">
              Start your camera and point it at the QR code on a customer's ticket.
            </p>
            <button
              onClick={startCamera}
              className="px-6 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition shadow-lg shadow-blue-500/20 inline-flex items-center gap-2"
            >
              <Camera className="w-4 h-4" />
              Start camera
            </button>
          </div>
        ) : (
          <div className="w-full max-w-3xl space-y-3">
            {/* Camera Feed - REACT WEBCAM */}
            <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/5">
              <Webcam
                ref={webcamRef}
                audio={false}
                playsInline
                screenshotFormat="image/jpeg"
                videoConstraints={{
                  facingMode: 'environment',
                  width: { ideal: 1280 },
                  height: { ideal: 720 }
                }}
                onUserMedia={() => setCameraError('')}
                onUserMediaError={handleUserMediaError}
                className="w-full h-auto"
                style={{ minHeight: '500px', maxHeight: '70vh' }}
              />

              {/* Camera blocked / failed — clear fallback instead of a black screen */}
              {cameraError && (
                <div className="absolute inset-0 z-20 flex flex-col items-center justify-center bg-black/90 px-6 text-center" data-testid="qr-camera-error">
                  <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center mb-4">
                    <AlertCircle className="w-7 h-7 text-red-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1.5">Camera unavailable</h3>
                  <p className="text-sm text-gray-300 max-w-xs mb-5">{cameraError}</p>
                  <button
                    onClick={retryCamera}
                    data-testid="qr-camera-retry"
                    className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition inline-flex items-center gap-2">
                    <Camera className="w-4 h-4" />
                    Try again
                  </button>
                </div>
              )}
              
              {/* Scanning frame overlay with dynamic colors */}
              {scanning && (
                <>
                  {/* Status indicator */}
                  <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[11px] font-semibold flex items-center gap-1.5 z-10 backdrop-blur-md ${
                    scanState === 'idle' ? 'bg-white/10 text-white' :
                    scanState === 'detecting' ? 'bg-blue-500/90 text-white' :
                    scanState === 'success' ? 'bg-emerald-500/90 text-white' :
                    scanState === 'warning' ? 'bg-amber-500/90 text-white' :
                    'bg-red-500/90 text-white'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full bg-current ${scanState === 'idle' ? 'animate-pulse' : ''}`}></span>
                    {scanState === 'idle' && 'Scanning'}
                    {scanState === 'detecting' && 'Found'}
                    {scanState === 'success' && 'Checked in'}
                    {scanState === 'warning' && 'Already checked in'}
                    {scanState === 'error' && 'Failed'}
                  </div>

                  {/* Cooldown indicator */}
                  {cooldownSeconds > 0 && (
                    <div className={`absolute top-12 left-3 px-3 py-1.5 rounded-lg text-[11px] font-medium shadow-xl z-10 flex items-center gap-2 backdrop-blur-md ${
                      scanState === 'success' ? 'bg-emerald-500/90 text-white' :
                      scanState === 'warning' ? 'bg-amber-500/90 text-white' :
                      scanState === 'error' ? 'bg-red-500/90 text-white' :
                      'bg-blue-500/90 text-white'
                    }`}>
                      <span className="w-5 h-5 rounded-full ring-2 ring-current/40 flex items-center justify-center text-xs font-bold">{cooldownSeconds}</span>
                      <span>Cooldown</span>
                    </div>
                  )}

                  {/* Scanning frame */}
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                    <div className={`relative w-64 h-64 sm:w-72 sm:h-72 transition-colors duration-300 ${
                      scanState === 'idle' ? 'text-white/60' :
                      scanState === 'detecting' ? 'text-blue-400' :
                      scanState === 'success' ? 'text-emerald-400' :
                      scanState === 'warning' ? 'text-amber-400' :
                      'text-red-400'
                    }`}>
                      <div className="absolute top-0 left-0 w-7 h-7 border-t border-l border-current rounded-tl-lg"></div>
                      <div className="absolute top-0 right-0 w-7 h-7 border-t border-r border-current rounded-tr-lg"></div>
                      <div className="absolute bottom-0 left-0 w-7 h-7 border-b border-l border-current rounded-bl-lg"></div>
                      <div className="absolute bottom-0 right-0 w-7 h-7 border-b border-r border-current rounded-br-lg"></div>
                    </div>
                  </div>

                  {/* Error message display */}
                  {scanState === 'error' && lastError && (
                    <div className="absolute top-12 left-1/2 transform -translate-x-1/2 bg-red-500/90 backdrop-blur-md text-white px-4 py-2 rounded-lg shadow-lg max-w-sm text-center z-10">
                      <p className="text-sm font-medium">{lastError}</p>
                    </div>
                  )}
                </>
              )}

              {/* Instructions overlay */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pt-10">
                <p className="text-white/80 text-center text-xs sm:text-sm">
                  Point camera at the QR code on the customer's ticket
                </p>
              </div>
            </div>

            {/* Stop Camera — subdued */}
            <button
              onClick={stopCamera}
              className="w-full py-2.5 text-gray-300 text-sm font-medium rounded-lg border border-white/10 hover:bg-white/5 transition inline-flex items-center justify-center gap-2"
            >
              <X className="w-4 h-4" />
              Stop camera
            </button>
          </div>
        )}
      </div>

      {/* Success Modal */}
      {showSuccessModal && lastCheckedIn && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl p-8 max-w-md w-full text-center transform animate-in zoom-in-95 duration-300 ${
            lastCheckedIn.alreadyCheckedIn 
              ? 'bg-yellow-50 border-4 border-yellow-400' 
              : 'bg-white'
          }`}>
            {lastCheckedIn.alreadyCheckedIn ? (
              <>
                <AlertCircle className="w-20 h-20 text-yellow-500 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Already Checked In</h2>
              </>
            ) : (
              <>
                <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Checked In!</h2>
              </>
            )}
            <p className="text-2xl font-semibold text-gray-700 mb-1">
              {lastCheckedIn.customerName || lastCheckedIn.customerEmail}
            </p>
            {lastCheckedIn.serviceName && (
              <p className="text-gray-600 mb-4">{lastCheckedIn.serviceName}</p>
            )}
            {cooldownSeconds > 0 && (
              <div className="mt-6">
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full border-4 mb-2 ${
                  lastCheckedIn.alreadyCheckedIn 
                    ? 'bg-yellow-100 border-yellow-500' 
                    : 'bg-green-100 border-green-500'
                }`}>
                  <span className={`text-3xl font-bold ${
                    lastCheckedIn.alreadyCheckedIn ? 'text-yellow-600' : 'text-green-600'
                  }`}>{cooldownSeconds}</span>
                </div>
                <p className="text-sm text-gray-500">Ready to scan next ticket in...</p>
                <p className="text-xs text-gray-400 mt-3">
                  💡 Customer can refresh their ticket to see check-in status
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Scans */}
      {recentScans.length > 0 && (
        <div className="border-t border-white/5 px-5 sm:px-6 py-4 max-h-64 overflow-y-auto">
          <h3 className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Recent scans</h3>
          <div className="space-y-1.5">
            {recentScans.map((scan, idx) => (
              <div
                key={idx}
                className={`px-3 py-2 rounded-lg flex items-start gap-2.5 ${
                  scan.status === 'success' ? 'bg-emerald-500/5 border border-emerald-500/20' :
                  scan.status === 'already_checked_in' ? 'bg-amber-500/5 border border-amber-500/20' :
                  'bg-red-500/5 border border-red-500/20'
                }`}
              >
                {scan.status === 'success' ? (
                  <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                ) : scan.status === 'already_checked_in' ? (
                  <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  {scan.status === 'error' ? (
                    <>
                      <p className="text-xs font-medium text-white">Scan failed</p>
                      <p className="text-[11px] text-red-300/80 truncate">{String(scan.error || 'Unknown error')}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs font-medium text-white truncate">
                        {scan.status === 'already_checked_in' ? 'Already checked in · ' : ''}{scan.customerName}
                      </p>
                      <p className="text-[11px] text-gray-400 truncate">
                        {scan.serviceName} · <span className="font-mono">{scan.confirmationCode}</span>
                      </p>
                    </>
                  )}
                </div>
                <span className="text-[11px] text-gray-500 flex-shrink-0">
                  {new Date(scan.checkedInAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QRScannerGun;
